import { createContext, useContext, useMemo } from 'react';
import * as DropdownMenu from 'components/dropdown-menu';
import * as CancelBid from 'components/pages/campaign-settings/campaign-auction/item-details/bids/CancelBidAlertDialog';
import {
    CampaignItemRoutes,
    useCampaignItemRoutes,
} from 'components/pages/campaign-settings/campaign-auction/useCampaignItemRoutes';
import { AuctionItem, Bid, Campaign } from 'types/domain';

interface IBidsOptionsContext {
    auctionItem: AuctionItem;
    campaign: Campaign;
    routes: CampaignItemRoutes;
    bid: Bid;
}

const BidsOptionsContext = createContext<IBidsOptionsContext>({
    auctionItem: {} as AuctionItem,
    campaign: {} as Campaign,
    routes: {} as CampaignItemRoutes,
    bid: {} as Bid,
});

interface BidsOptionsProps extends DropdownMenu.DropdownMenuProps {
    auctionItem: AuctionItem;
    campaign: Campaign;
    bid: Bid;
}

interface BidsOptionsCancelBidProps extends DropdownMenu.DropdownMenuItemProps {
    onCancelBid?: () => void;
}

const BidsOptions = (props: BidsOptionsProps) => {
    const { auctionItem, campaign, bid, children, ...rest } = props;
    const routes = useCampaignItemRoutes();

    const contextValue = useMemo(
        () => ({ auctionItem, campaign, routes, bid }),
        [auctionItem, campaign, routes, bid],
    );

    return (
        <BidsOptionsContext.Provider value={contextValue}>
            <DropdownMenu.Root {...rest}>{children}</DropdownMenu.Root>
        </BidsOptionsContext.Provider>
    );
};

const BidsOptionsContent = DropdownMenu.Content;
const BidsOptionsTrigger = DropdownMenu.Trigger;

const BidsOptionsCancelBid = (props: BidsOptionsCancelBidProps) => {
    const { onCancelBid, ...rest } = props;
    const { auctionItem, campaign, bid } = useContext(BidsOptionsContext);
    const dropdownDisabledClass = !bid.can_disqualify
        ? '!cursor-not-allowed hover:!bg-transparent opacity-60'
        : '';
    const buttonDisabledClass = !bid.can_disqualify ? 'pointer-events-none ' : '';

    return (
        <DropdownMenu.Item
            {...rest}
            className={`!p-0 ${dropdownDisabledClass}`}
            onClick={e => e.preventDefault()}
        >
            <CancelBid.Root
                campaignId={campaign.id}
                bid={bid}
                auctionId={auctionItem.auction_id}
                onCancelBid={onCancelBid}
            >
                <CancelBid.Trigger asChild={true}>
                    <p className={`px-1 py-2 text-sm ${buttonDisabledClass}`}>Cancel bid</p>
                </CancelBid.Trigger>
            </CancelBid.Root>
        </DropdownMenu.Item>
    );
};

const Root = BidsOptions;
const Content = BidsOptionsContent;
const Trigger = BidsOptionsTrigger;
const Cancel = BidsOptionsCancelBid;

export {
    BidsOptions,
    BidsOptionsContent,
    BidsOptionsTrigger,
    BidsOptionsCancelBid,
    //
    Root,
    Content,
    Trigger,
    Cancel,
};
